/* eslint-disable */
import { StandaloneSearchBox } from '@react-google-maps/api';
import { useRef } from 'react';

import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { TooltipIcon } from '../tooltip/TooltipIcon';

interface CustomInputProperties {
  inputLabel?: string;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue: (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
    geoLocation: any[],
  ) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
  tooltipText?: string;
}

/**
 * Custom address input box with google places api integrated
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.value - The value prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.tooltipText - The tooltipText prop
 */
function CustomAddressInput({
  inputLabel,
  name,
  placeholder,
  value,
  onChangeValue,
  valueRequired,
  readOnly,
  error,
  errorMessage,
  tooltipText,
}: CustomInputProperties) {
  const inputReference = useRef<google.maps.places.SearchBox | null>(null);

  const { isLoaded } = useGoogleMaps();

  /**
   * Handle input change
   * @param {any} changeValue - The changeValue
   */
  const handleValueChange = (changeValue: string | number) => {
    onChangeValue(name, changeValue, null, null, []);
  };

  /**
   * Handles reference load for the StandaloneSearchBox.
   * @param {any} reference - The reference
   */
  const handleReferenceLoad = (reference: google.maps.places.SearchBox | null) => {
    inputReference.current = reference;
  };

  /**
   * Get the Geo Location details
   */
  const getGeoLocation = (place: any) => {
    // Country-specific mapping
    const levelMapping: Record<string, Record<string, { name: string; key: string }>> = {
      // India
      IN: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_3: { name: 'Taluk', key: 'taluk' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'State', key: 'state' },
        country: { name: 'Country', key: 'country' },
      },

      // Saudi Arabia
      SA: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Governorate', key: 'governorate' },
        administrative_area_level_1: { name: 'Region', key: 'region' },
        country: { name: 'Country', key: 'country' },
      },

      // United Arab Emirates
      AE: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_1: { name: 'Emirate', key: 'emirate' },
        country: { name: 'Country', key: 'country' },
      },

      // Bahrain
      BH: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'Governorate', key: 'governorate' },
        country: { name: 'Country', key: 'country' },
      },

      // Kuwait
      KW: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'Governorate', key: 'governorate' },
        country: { name: 'Country', key: 'country' },
      },

      // Ethiopia
      ET: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Zone', key: 'zone' },
        administrative_area_level_1: { name: 'Region', key: 'region' },
        country: { name: 'Country', key: 'country' },
      },

      // China
      CN: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Prefecture', key: 'prefecture' },
        administrative_area_level_1: { name: 'Province', key: 'province' },
        country: { name: 'Country', key: 'country' },
      },
    };

    // Get country code first
    let countryCode = '';

    place.address_components?.forEach(({ short_name, types }: any) => {
      if (types.includes('country')) {
        countryCode = short_name;
      }
    });

    const mapping = levelMapping[countryCode] || {
      locality: { name: 'City', key: 'city' },
      administrative_area_level_4: {
        name: 'Administrative Area Level 4',
        key: 'level4',
      },
      administrative_area_level_3: {
        name: 'Administrative Area Level 3',
        key: 'level3',
      },
      administrative_area_level_2: {
        name: 'Administrative Area Level 2',
        key: 'level2',
      },
      administrative_area_level_1: {
        name: 'Administrative Area Level 1',
        key: 'level1',
      },
      country: { name: 'Country', key: 'country' },
    };

    const result: Array<{
      name: string;
      key: string;
      value: string;
    }> = [];

    place.address_components?.forEach(({ long_name, types }: any) => {
      const type = types.find((t: any) => mapping[t as keyof typeof mapping]);

      if (type) {
        const { name, key } = mapping[type];

        result.push({
          name,
          key,
          value: long_name,
        });
      }
    });
    return result;
  };

  /**
   * Just a place change
   */
  const handlePlaceChange = () => {
    if (inputReference.current) {
      const places = inputReference.current.getPlaces();

      if (places?.length) {
        const place = places[0];
        const geoLocations: any[] = getGeoLocation(place);

        const formatted = place.formatted_address || place.name || '';

        let lat: number | null = null;
        let lng: number | null = null;

        if (place.geometry?.location) {
          lat = place.geometry.location.lat();
          lng = place.geometry.location.lng();
        }
        onChangeValue(name, formatted, lat, lng, geoLocations);
      }
    }
  };

  /**
   * Geocode the typed address string when user presses Enter or blurs.
   * @param {string} addressString - The address string to geocode
   */
  const geocodeAddress = (addressString: string) => {
    if (!addressString || !window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addressString }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const place = results[0];
        const geoLocations = getGeoLocation(place);
        const formatted = place.formatted_address || addressString;
        let lat: number | null = null;
        let lng: number | null = null;

        if (place.geometry?.location) {
          lat = place.geometry.location.lat();
          lng = place.geometry.location.lng();
        }
        onChangeValue(name, formatted, lat, lng, geoLocations);
      }
    });
  };

  return (
    <div className="custom-address-input-wrap">
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
          {tooltipText && <TooltipIcon text={tooltipText} />}
        </p>
      )}
      <div className={`custom-input-container ${error && 'error'}`}>
        {isLoaded && (
          <StandaloneSearchBox
            onLoad={(searchBox) => handleReferenceLoad(searchBox as google.maps.places.SearchBox)}
            onPlacesChanged={handlePlaceChange}>
            <input
              type="text"
              placeholder={placeholder}
              value={value !== null ? value : undefined}
              onChange={(event) => handleValueChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  geocodeAddress(event.currentTarget.value);
                }
              }}
              onBlur={(event) => {
                const textValue = event.currentTarget.value;
                setTimeout(() => {
                  geocodeAddress(textValue);
                }, 200);
              }}
              required={valueRequired}
              readOnly={readOnly}
            />
          </StandaloneSearchBox>
        )}
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

CustomAddressInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  error: false,
  errorMessage: '',
  tooltipText: '',
};

export default CustomAddressInput;
